import { type Option } from '@allesauto/core/searchDropdown';
import { SchemaTypes } from '@allesauto/graphql';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { consts, GTMFilterCategory } from '@allesauto/utils/consts';

interface FilterDataLayerValues {
  category: string;
  name: string;
  values: string;
}

const pushFilterValueToDataLayer = (filterValues: FilterDataLayerValues) => {
  pushAnalyticsEvent({
    event: 'filter',
    filter_category: filterValues.category,
    filter_name: filterValues.name,
    filter_values: filterValues.values,
  });
};

const getPageType = (routePath: string) => {
  if (routePath === '/[dealer]') return consts.GTM_PAGE_TYPES.HOME;
  if (routePath.match('/account') || routePath.match('/about'))
    return consts.GTM_PAGE_TYPES.ACCOUNT;
  if (
    routePath.match('/buy-car-online') ||
    routePath.match('/auto-online-kaufen')
  )
    return consts.GTM_PAGE_TYPES.BUY_CAR_ONLINE;
  if (routePath.match('/car-accessories'))
    return consts.GTM_PAGE_TYPES.CAR_ACCESSORIES;
  if (
    routePath.match('/auto-verkaufen-online') ||
    routePath.match('/car-sell') ||
    routePath.match('/sell-car-private')
  )
    return consts.GTM_PAGE_TYPES.CAR_SELL;
  if (routePath.match('/careeer') || routePath.match('/kerriere'))
    return consts.GTM_PAGE_TYPES.CAREER;
  if (routePath.match('/cars') || routePath.match('/vehicles'))
    return consts.GTM_PAGE_TYPES.CARS;
  if (routePath.match('/checkout')) return consts.GTM_PAGE_TYPES.CHECKOUT;
  if (routePath.match('/consulting-information'))
    return consts.GTM_PAGE_TYPES.CONSULTING_INFORMATION;
  if (routePath.match('/contact') || routePath.match('/kontakt'))
    return consts.GTM_PAGE_TYPES.CONTACT_SUPPORT;
  if (
    routePath.match('/dealer-overview') ||
    routePath.match('/autohaendler-in-der-naehe')
  )
    return consts.GTM_PAGE_TYPES.DEALER_OVERVIEW;
  if (routePath.match('/garage') || routePath.match('/online-garage'))
    return consts.GTM_PAGE_TYPES.GARAGE;
  if (routePath.match('/help-faqs')) return consts.GTM_PAGE_TYPES.HELP_FAQS;
  if (
    routePath.match('/info-for-dealers') ||
    routePath.match('/autohaendler-plattform')
  )
    return consts.GTM_PAGE_TYPES.INFO_FOR_DEALERS;
  if (routePath.match('/newsletter')) return consts.GTM_PAGE_TYPES.NEWSLETTER;
  if (routePath.match('/offer-overview') || routePath.match('/autohaus-online'))
    return consts.GTM_PAGE_TYPES.OFFER_OVERVIEW;
  if (
    routePath.match('/payment-methods') ||
    routePath.match('/autokauf-bezahlung')
  )
    return consts.GTM_PAGE_TYPES.PAYMENT_METHODS;
  if (routePath.match('/price-check')) return consts.GTM_PAGE_TYPES.PRICE_CHECK;
  if (
    routePath.match('/private-sale') ||
    routePath.match('/sale-to-dealer') ||
    routePath.match('/auto-an-haendler-verkaufen') ||
    routePath.match('/auto-verkaufen-privat')
  )
    return consts.GTM_PAGE_TYPES.PRIVATE_SALE;
  if (routePath.match('/service-booking'))
    return consts.GTM_PAGE_TYPES.SERVICE_BOOKING;
  if (routePath.match('/shoppingCart')) return consts.GTM_PAGE_TYPES.CART;
  if (routePath.match('/tradein') || routePath.match('/inzahlungnahme-auto'))
    return consts.GTM_PAGE_TYPES.TRADE_IN;
  if (routePath.match('/whitelabel')) return consts.GTM_PAGE_TYPES.WHITE_LABEL;
  if (routePath.match('/workshop-services'))
    return consts.GTM_PAGE_TYPES.WORKSHOP_SERVICES;
};

/**
 * Hook that pushes global page information to the GTM's dataLayer
 */
export const useGTM = () => {
  const setVirtualPageview = (
    path: string,
    virtualPath: string,
    pageTitle?: string
  ) => {
    const pageType = getPageType(path) ?? getPageType(virtualPath);
    const virtualPageName = pageTitle
      ? pageTitle.substring(0, pageTitle.indexOf('|') - 1)
      : '';

    if (!pageType) {
      return;
    }

    pushAnalyticsEvent({
      event: 'virtual_pageview',
      page_type: pageType,
      // TODO: we cannot differentiate yet between user_type: [dealer, customer]
      user_type: 'customer',
      // TODO: include dealer name when functionality is available
      dealer_name: 'N/A',
      virtual_page_url: virtualPath,
      virtual_page_name: virtualPageName?.toLowerCase(),
    });
  };

  const setFilterInformation = (filterCategory: string, value: any) => {
    switch (filterCategory) {
      case GTMFilterCategory.LOCATION: {
        const categoryName = 'standort';

        if (value.location?.label) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'ort',
            values: value.location.label,
          });
        }

        if (value.radius) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'umkreis',
            values: value.radius,
          });
        }
        break;
      }

      case GTMFilterCategory.VEHICLE_TYPE: {
        const categoryName = 'fahrzeugtyp';

        if (value.vehicleType?.length > 0) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'fahrzeugtyp',
            values: value.vehicleType.join(', '),
          });
        }

        if (value.doors?.length > 0) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'türen',
            values: value.doors.join(', '),
          });
        }

        let numberOfSeats = '';

        if (value.minSeats && !value.maxSeats)
          numberOfSeats = `ab ${value.minSeats} sitzplätze”`;
        if (!value.minSeats && value.maxSeats)
          numberOfSeats = `bis ${value.maxSeats} sitzplätze”`;
        if (value.minSeats && value.maxSeats)
          numberOfSeats = `${value.minSeats}-${value.maxSeats} sitzplätze”`;

        if (numberOfSeats !== '') {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'sitzplätze',
            values: numberOfSeats,
          });
        }
        break;
      }

      case GTMFilterCategory.MAKE_MODEL: {
        const categoryName = 'marke-modell';

        if (value.make) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'marke',
            values: value.make,
          });
        }

        if (value.model) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'modell',
            values: value.model,
          });
        }
        break;
      }

      case GTMFilterCategory.PRICE:
        pushFilterValueToDataLayer({
          category: 'preis',
          name: value.key,
          values: `${value.min} - ${value.max}`,
        });
        break;

      case GTMFilterCategory.VEHICLE_CONDITION: {
        const filterOptions = [];

        if (value.condition.length > 0) filterOptions.push(...value.condition);
        if (value.mileage) filterOptions.push(`${value.mileage} km`);

        if (value.minYear && !value.maxYear)
          filterOptions.push(`EZ ${value.minYear}`);
        if (!value.minYear && value.maxYear)
          filterOptions.push(`EZ ${value.maxYear}`);
        if (value.minYear && value.maxYear)
          filterOptions.push(`EZ ${value.minYear}-${value.maxYear}`);

        if (value.maxOwners)
          filterOptions.push(`max. ${value.maxOwners} vorh. Fahrzeughalter`);

        pushFilterValueToDataLayer({
          category: 'fahrzeugzustand',
          name: 'fahrzeugzustand',
          values: filterOptions.join(', '),
        });
        break;
      }

      case GTMFilterCategory.FUEL: {
        const fuelKey = Object.keys(value)[0];
        const fuelValue = value[fuelKey];

        if (Array.isArray(fuelValue)) {
          pushFilterValueToDataLayer({
            category: 'kraftstoff',
            name: fuelKey,
            values: fuelValue.join(', '),
          });
        } else {
          pushFilterValueToDataLayer({
            category: 'kraftstoff',
            name: fuelKey,
            values: String(fuelValue), // converting to string if not an array
          });
        }
        break;
      }

      case GTMFilterCategory.DRIVE: {
        const filterOptions = [];

        if (value.gearBox.length > 0) filterOptions.push(...value.gearBox);
        if (value.maxCubicCapacity)
          filterOptions.push(`${value.maxCubicCapacity} ccm`);
        if (value.power.min && value.power.max)
          filterOptions.push(
            `${value.power.min}-${value.power.max} ${value.power.key}`
          );

        pushFilterValueToDataLayer({
          category: 'antrieb',
          name: 'antrieb',
          values: filterOptions.join(', '),
        });
        break;
      }

      case GTMFilterCategory.BODY_COLORS:
        pushFilterValueToDataLayer({
          category: 'fahrzeugfarbe',
          name: 'fahrzeugfarbe',
          values: value.join(', '),
        });
        break;

      case GTMFilterCategory.EQUIPMENT: {
        const categoryName = 'ausstattung';

        if (value.aircon?.length > 0) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'klimatisierung',
            values: value.aircon.join(', '),
          });
        }

        if (value.comfort?.length > 0) {
          const comfort = value.comfort.map(
            (comfortVal: Option) => comfortVal.label
          );

          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'komfort',
            values: comfort.join(', '),
          });
        }

        if (value.security?.length > 0) {
          const security = value.security.map(
            (securityVal: Option) => securityVal.label
          );

          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'sicherheit',
            values: security.join(', '),
          });
        }

        if (value.entertainment?.length > 0) {
          const entertainment = value.entertainment.map(
            (entertainmentVal: Option) => entertainmentVal.label
          );

          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'entertainment',
            values: entertainment.join(', '),
          });
        }
        break;
      }

      case GTMFilterCategory.MERCHANT_LOCATION: {
        const categoryName = 'Händlerstandort';

        if (value.merchantLocation?.length > 0) {
          const merchantLocation = value.merchantLocation.map(
            (merchantLocationVal: Option) => merchantLocationVal.label
          );

          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'händlerstandort',
            values: merchantLocation.join(', '),
          });
        }
        break;
      }

      case GTMFilterCategory.INTERIOR: {
        const categoryName = 'interieur';

        if (value.types?.length > 0) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'innentyp',
            values: value.types.join(', '),
          });
        }

        if (value.colors?.length > 0) {
          pushFilterValueToDataLayer({
            category: categoryName,
            name: 'innenfarbe',
            values: value.colors.join(', '),
          });
        }
        break;
      }

      case GTMFilterCategory.EMISSION: {
        const filterOptions = [];

        if (value.emissionClass)
          filterOptions.push(`mind. ${value.emissionClass}`);
        if (value.co2Efficiency)
          filterOptions.push(`mind. ${value.co2Efficiency}`);
        if (value.emissionSticker?.length > 0)
          filterOptions.push(...value.emissionSticker);

        filterOptions.push(value.particleFilter);

        pushFilterValueToDataLayer({
          category: 'emissionen',
          name: 'emissionen',
          values: filterOptions.join(', '),
        });
        break;
      }
    }
  };

  const mapCheckoutVehiclesToDataLayer = (
    vehicleItems: SchemaTypes.CartItem[],
    listName?: string
  ) => {
    return vehicleItems.map((item, index) => {
      const product = item.product as SchemaTypes.Vehicle;
      const payment: SchemaTypes.CartItemPayment = item.payment;
      return {
        item_name: `${product.makeName?.toLowerCase()} ${product.modelName?.toLowerCase()}`,
        item_id: product.id,
        price: payment.price,
        item_brand: product.makeName?.toLowerCase(),
        item_category: 'car',
        item_category2: product.vehicleCondition?.toLowerCase(),
        item_variant: product.bodyColor?.toLowerCase(),
        item_list_name: listName || 'checkout',
        item_list_id: listName || 'checkout',
        index: index,
        quantity: item.quantity,
      };
    });
  };

  const mapCheckoutAccessoriesToDataLayer = (
    accessoryItems: SchemaTypes.CartItem[],
    listName?: string
  ) => {
    return accessoryItems.map((item, index) => {
      const product = item.product as SchemaTypes.Accessory;

      return {
        item_name: product.title.toLowerCase(),
        item_id: product.productId,
        price: product.unitPrice || '-',
        item_brand: '',
        item_category: 'accessory',
        item_variant: product.specification,
        item_list_name: listName || 'checkout',
        item_list_id: listName || 'checkout',
        index: index,
        quantity: item.quantity,
      };
    });
  };

  const getEcommerceValue = (
    item: SchemaTypes.CartItem,
    isVehicle?: boolean
  ) => {
    if (isVehicle) {
      const payment: SchemaTypes.CartItemPayment = item.payment;
      return payment.price;
    }

    const product = item.product as SchemaTypes.Accessory;
    return product.sumPrice;
  };

  return {
    setVirtualPageview,
    mapCheckoutVehiclesToDataLayer,
    mapCheckoutAccessoriesToDataLayer,
    setFilterInformation,
    getEcommerceValue,
  };
};
